import { CrmHeader, CrmPagination, CrmContacts } from '../components/crm/CrmUi';
import { useCrmListState, useCrmNavigation } from '../hooks/useCrmNavigation';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Plus,
  Search,
  UserPlus,
  Loader2,
  Building2,
  User,
  UserCog,
  Clock,
  Bookmark,
  Save,
  Download,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from '../hooks/usePermissions';
import { leadsAPI, leadSegmentsAPI, usersAPI } from '../utils/api';
import {
  LeadFilters,
  LeadStato,
  LeadSource,
  LEAD_STATI,
  LEAD_STATO_LABEL,
  LEAD_SOURCES,
  LEAD_SOURCE_LABEL,
} from '../types/lead';
import { LeadSegmentFilters } from '../types/leadSegment';
import LeadStatoBadge from '../components/lead/LeadStatoBadge';
import LeadSourceBadge from '../components/lead/LeadSourceBadge';
import LeadsViewToggle from '../components/lead/LeadsViewToggle';
import LeadScoreBar from '../components/lead/LeadScoreBar';

function parseMulti<T extends string>(value: string | null, allowed: readonly T[]): T[] {
  if (!value) return [];
  return value.split(',').filter((v): v is T => (allowed as readonly string[]).includes(v));
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT');
}

export default function Leads() {
  const { openDetail, openCreate } = useCrmNavigation('/leads');
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('leads:create');
  const canExport = hasPermission('leads:export');
  const { searchParams, setSearchParams, page, updateParam } = useCrmListState();
  const [saveSegmentOpen, setSaveSegmentOpen] = useState(false);
  const [appliedSegmentId, setAppliedSegmentId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const segmentId = searchParams.get('segment_id') || '';
  const search = searchParams.get('search') || '';
  const statoFilter = parseMulti<LeadStato>(searchParams.get('stato'), LEAD_STATI);
  const sourceFilter = parseMulti<LeadSource>(searchParams.get('source'), LEAD_SOURCES);
  const agenteFilter = searchParams.get('agente') || '';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';

  const filters: LeadFilters = useMemo(
    () => ({
      page,
      limit: 20,
      stato: statoFilter.length ? statoFilter : undefined,
      source: sourceFilter.length ? sourceFilter : undefined,
      agente_id: agenteFilter || undefined,
      search: search || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
    [page, statoFilter, sourceFilter, agenteFilter, search, dateFrom, dateTo]
  );

  const { data: leadsData, isLoading, isError, refetch } = useQuery({
    queryKey: ['leads', filters],
    queryFn: async () => {
      const res = await leadsAPI.getLeads(filters);
      return res.data;
    },
  });

  // Cono di visibilità (issue #54): scoped al proprio cono per chi non ha users:view
  const { data: agents } = useQuery({
    queryKey: ['users-hierarchy'],
    queryFn: async () => {
      const res = await usersAPI.getHierarchy();
      return res.data;
    },
  });

  // Active segment (when URL has segment_id)
  const { data: activeSegment } = useQuery({
    queryKey: ['segment', segmentId],
    queryFn: async () => {
      const res = await leadSegmentsAPI.getSegment(segmentId);
      return res.data;
    },
    enabled: !!segmentId,
  });

  // On first load of segment: apply its filters to URL
  useEffect(() => {
    if (!activeSegment) return;
    if (appliedSegmentId === activeSegment.id) return;

    const f = (activeSegment.filters || {}) as LeadSegmentFilters;
    const next = new URLSearchParams();
    next.set('segment_id', activeSegment.id);
    if (f.search) next.set('search', f.search);
    if (f.stato && f.stato.length) next.set('stato', f.stato.join(','));
    if (f.source && f.source.length) next.set('source', f.source.join(','));
    if (f.agente_id) next.set('agente', f.agente_id);
    if (f.date_from) next.set('date_from', f.date_from);
    if (f.date_to) next.set('date_to', f.date_to);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
    setAppliedSegmentId(activeSegment.id);
  }, [activeSegment, appliedSegmentId, setSearchParams]);

  const currentSegmentFilters: LeadSegmentFilters = useMemo(
    () => ({
      page: 1,
      limit: 20,
      search: search || undefined,
      stato: statoFilter.length ? statoFilter : undefined,
      source: sourceFilter.length ? sourceFilter : undefined,
      agente_id: agenteFilter || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
    [search, statoFilter, sourceFilter, agenteFilter, dateFrom, dateTo]
  );

  const updateSegmentMutation = useMutation({
    mutationFn: () =>
      leadSegmentsAPI.updateSegment(segmentId, {
        filters: currentSegmentFilters,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['segment', segmentId] });
      queryClient.invalidateQueries({ queryKey: ['segments'] });
      toast.success('Filtri segmento aggiornati');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    },
  });

  const createSegmentMutation = useMutation({
    mutationFn: (data: { name: string; description: string }) =>
      leadSegmentsAPI.createSegment({
        name: data.name,
        description: data.description || undefined,
        filters: currentSegmentFilters,
        is_active: true,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['segments'] });
      toast.success('Segmento creato');
      setSaveSegmentOpen(false);
      const next = new URLSearchParams(searchParams);
      next.set('segment_id', res.data.id);
      setSearchParams(next, { replace: true });
      setAppliedSegmentId(res.data.id);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    },
  });

  const clearSegment = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('segment_id');
    setSearchParams(next, { replace: true });
    setAppliedSegmentId(null);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params: Record<string, unknown> = {};
      if (segmentId) {
        params.segment_id = segmentId;
      } else {
        if (search) params.search = search;
        if (statoFilter.length) params.stato = statoFilter;
        if (sourceFilter.length) params.source = sourceFilter;
        if (agenteFilter) params.agente_id = agenteFilter;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const res = await leadSegmentsAPI.exportCSV(params);
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`CSV scaricato: ${filename}`);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Errore nell'export");
    } finally {
      setIsExporting(false);
    }
  };

  const leads = leadsData?.data || [];
  const pagination = leadsData?.pagination;

  const toggleStato = (s: LeadStato) => {
    const next = statoFilter.includes(s)
      ? statoFilter.filter((x) => x !== s)
      : [...statoFilter, s];
    updateParam({ stato: next });
  };

  const toggleSource = (s: LeadSource) => {
    const next = sourceFilter.includes(s)
      ? sourceFilter.filter((x) => x !== s)
      : [...sourceFilter, s];
    updateParam({ source: next });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <CrmHeader title="Lead" description="Contatti commerciali pre-vendita">
          <LeadsViewToggle current="list" />
          {canExport && (
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Esporta CSV
            </button>
          )}
          {canCreate && (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
              Nuovo Lead
            </button>
          )}
      </CrmHeader>

      {/* Active segment banner */}
      {activeSegment && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm text-indigo-900">
            <Bookmark className="h-4 w-4" />
            <span>
              Stai esplorando il segmento <strong>«{activeSegment.name}»</strong>
              {activeSegment.description && (
                <span className="text-indigo-700"> · {activeSegment.description}</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => updateSegmentMutation.mutate()}
              disabled={updateSegmentMutation.isPending}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-100 disabled:opacity-50"
              title="Aggiorna i filtri del segmento con quelli correnti"
            >
              {updateSegmentMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Aggiorna filtri
            </button>
            <button
              onClick={clearSegment}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-indigo-700 hover:bg-indigo-100 rounded"
              title="Rimuovi associazione al segmento"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {Array.from(searchParams.keys()).some(key => key !== 'page') && (
        <button onClick={() => updateParam(Object.fromEntries(Array.from(searchParams.keys()).map(key => [key, null])))} className="text-sm text-blue-600 hover:underline">Azzera filtri</button>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome, email, telefono, azienda..."
              value={search}
              onChange={(e) => updateParam({ search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={agenteFilter}
            onChange={(e) => updateParam({ agente: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutti gli agenti</option>
            {agents?.filter((u) => u.is_active).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => updateParam({ date_from: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            title="Data dal"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => updateParam({ date_to: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            title="Data al"
          />
        </div>

        {/* Stato chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Stato:</span>
          {LEAD_STATI.map((s) => {
            const active = statoFilter.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStato(s)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {LEAD_STATO_LABEL[s]}
              </button>
            );
          })}
        </div>

        {/* Source chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Origine:</span>
          {LEAD_SOURCES.map((s) => {
            const active = sourceFilter.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleSource(s)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {LEAD_SOURCE_LABEL[s]}
              </button>
            );
          })}
        </div>

        {/* Segment actions */}
        {!activeSegment && (
          <div className="flex justify-end pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setSaveSegmentOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100"
              title="Salva gli attuali filtri come segmento riusabile"
            >
              <Bookmark className="h-4 w-4" />
              Salva come segmento
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {isError ? (
          <div role="alert" className="p-8 text-center text-gray-600">
            <p>Impossibile caricare i risultati.</p>
            <button onClick={() => refetch()} className="mt-3 text-blue-600 hover:underline">Riprova</button>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <UserPlus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun lead trovato</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contatti
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Origine
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ultimo contatto
                </th>

              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  onClick={() => openDetail(lead.id)}
                  tabIndex={0} aria-label={`Apri ${lead.denominazione_completa}`}
                  onKeyDown={e => { if (e.target === e.currentTarget && e.key === 'Enter') openDetail(lead.id); }} className="hover:bg-gray-50 focus:bg-blue-50 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {lead.tipo === 'azienda' ? (
                          <Building2 className="h-8 w-8 text-blue-500" />
                        ) : (
                          <User className="h-8 w-8 text-gray-400" />
                        )}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {lead.denominazione_completa || '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {lead.tipo === 'azienda' ? 'Azienda' : 'Persona Fisica'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <CrmContacts email={lead.email} telefono={lead.telefono} />
                  </td>
                  <td className="px-6 py-4">
                    <LeadStatoBadge stato={lead.stato} />
                  </td>
                  <td className="px-6 py-4">
                    <LeadScoreBar score={lead.score} />
                  </td>
                  <td className="px-6 py-4">
                    <LeadSourceBadge source={lead.source} detail={lead.source_detail} />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {lead.agente_nome ? (
                      <span className="inline-flex items-center gap-1">
                        <UserCog className="h-4 w-4 text-gray-400" />
                        {lead.agente_nome}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-4 w-4 text-gray-400" />
                      {formatDate(lead.ultimo_contatto_at)}
                    </span>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        )}

        <CrmPagination pagination={pagination} label="lead" onPage={page => updateParam({ page: String(page) })} />
      </div>



      {saveSegmentOpen && (
        <SaveSegmentModal
          onClose={() => setSaveSegmentOpen(false)}
          onSave={(values) => createSegmentMutation.mutate(values)}
          isSaving={createSegmentMutation.isPending}
        />
      )}
    </div>
  );
}

function SaveSegmentModal({
  onClose,
  onSave,
  isSaving,
}: {
  onClose: () => void;
  onSave: (values: { name: string; description: string }) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Salva come segmento</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) {
              toast.error('Il nome è obbligatorio');
              return;
            }
            onSave({ name: name.trim(), description: description.trim() });
          }}
          className="p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={200}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Es. Lead qualificati dal Sud Italia"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="(opzionale) a cosa serve questo segmento"
            />
          </div>
          <p className="text-xs text-gray-500">
            I filtri attualmente applicati (stato, source, agente, search, date range)
            verranno salvati nel segmento. Puoi modificarli in qualsiasi momento riaprendo il
            segmento.
          </p>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              Crea segmento
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
